import { Component, EventEmitter, inject, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { SVG_ICONS } from "@frontend/app/models/icons";
import { SharedModule } from "@frontend/app/shared/shared.module";
import { ToastrService } from "@frontend/app/serivces/toastr.service";

type DocumentObject = {
  Category: string;
  FileName: string;
  MimeType: string;
  Base64: string;
};

@Component({
  selector: "app-document-upload",
  imports: [CommonModule, SharedModule],
  templateUrl: "./document-upload.component.html",
  styleUrl: "./document-upload.component.scss",
})
export class DocumentUploadComponent {
  private toastService = inject(ToastrService);
  @Input() label = "Upload Document";
  @Input() category = "";
  @Input() documents: DocumentObject[] = [];
  @Input() displayAs: "default" | "profile" = "default";

  get acceptTypes(): string {
    return this.displayAs === "profile" ? "image/jpeg,image/png,image/jpg" : ".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpg,image/jpeg";
  }

  @Output() documentsChange = new EventEmitter<DocumentObject[]>();
  @Output() documentRemove = new EventEmitter<DocumentObject>();

  readonly SVG_ICONS = SVG_ICONS;

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const files = Array.from(input.files);
    const newDocuments: Promise<DocumentObject>[] = [];
    const allowedImageTypes = ["image/jpeg", "image/png", "image/jpg"];
    const allowedDocTypes = [...allowedImageTypes, "application/pdf"];

    for (const file of files) {
      const maxSize = 2 * 1024 * 1024;
      const fileType = file.type.toLowerCase();
      if (file.size > maxSize) {
        this.toastService.warning(`File "${file.name}" exceeds the 2MB size limit.`);
        continue;
      }

      if (this.displayAs === "profile") {
        if (!allowedImageTypes.includes(fileType)) {
          this.toastService.warning("Only JPG, PNG images are allowed for profile pictures.");
          continue;
        }
      } else if (!allowedDocTypes.includes(fileType)) {
        this.toastService.warning("Only PDF, JPG, and PNG files are allowed.");
        continue;
      }

      newDocuments.push(this.readFileAsBase64(file));
    }

    Promise.all(newDocuments)
      .then(docs => {
        if (docs.length > 0) {
          this.documentsChange.emit(docs);
        }
      })
      .catch(error => {
        console.error("Error processing one or more files:", error);
        alert("An error occurred while uploading files. Please try again.");
      })
      .finally(() => {
        input.value = "";
      });
  }

  private readFileAsBase64(file: File): Promise<DocumentObject> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        resolve({
          Category: this.category,
          FileName: file.name,
          MimeType: file.type,
          Base64: reader.result as string,
        });
      };
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  }

  removeFile(docToRemove: DocumentObject): void {
    this.documentRemove.emit(docToRemove);
  }

  openFile(docToOpen: DocumentObject): void {
    if (docToOpen?.Base64) {
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(
          `<iframe src="${docToOpen.Base64}" frameborder="0"
             style="border:0; top:0; left:0; width:100%; height:100%;"
             allowfullscreen></iframe>`
        );
      }
    }
  }
}
